import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import { React } from 'Imports';
import { isEncompass } from '$Utilities/integrationPlatform';
import SingleSelect from '$Components/FilterComponents/Filters/SingleSelect';
import { useEffect, useState } from 'react';
import { IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { TelematicProviderDriverResponse, VideoEventResponse } from '$Generated/api';
import { VideoEventService } from '$State/VideoEventFreezerService';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';

interface EditEventDriverDialogProps {
    isOpen: boolean;
    videoEvent: VideoEventResponse;
    currentDriverIntegrationId: string | null;
    onClose: () => void;
    onDriverUpdated: (integrationId: string, fullname: string, email: string, phoneNumber: string) => void;
}

const EditEventDriverDialog = (props: EditEventDriverDialogProps) => {
    const { isOpen, currentDriverIntegrationId, videoEvent, onClose, onDriverUpdated } = props;
    const [driverOptions, setDriverOptions] = useState<IDropdownItem[]>([]);
    const [selectedDriverIntegrationId, setSelectedDriverIntegrationId] = useState<string | null>(null);
    const { showSnackbarMessage } = useAppSnackbar();

    useEffect(() => {
        if (isOpen) {
            setSelectedDriverIntegrationId(currentDriverIntegrationId);
        }
    }, [isOpen, currentDriverIntegrationId]);

    useEffect(() => {
        const init = async () => {
            await IntegrationPartnerDataService.getDrivers();
            const drivers = IntegrationPartnerDataService.getState().driversResults.data as TelematicProviderDriverResponse[];
            const driverOptions = drivers
                ?.filter((d) => d.isActive !== false)
                .map((d) => {
                    return {
                        label: `${d.firstName} ${d.lastName}`,
                        value: d.id,
                    } as IDropdownItem;
                });
            setDriverOptions(driverOptions ?? []);
        };

        init();
    }, []);

    const handleOnSubmit = async () => {
        try {
            if (selectedDriverIntegrationId === null) {
                await VideoEventService.removeDriver(videoEvent.id!);
                onDriverUpdated('', '', '', '');
            } else {
                const drivers = IntegrationPartnerDataService.getState().driversResults.data as TelematicProviderDriverResponse[];
                const driver = drivers.find((d) => d.id === selectedDriverIntegrationId);
                await VideoEventService.updateDriver(videoEvent.id!, { ...driver });
                onDriverUpdated(
                    driver?.id ?? '',
                    driver?.firstName + ' ' + driver?.lastName,
                    driver?.email ?? '',
                    driver?.phoneNumber ?? '',
                );
            }
            showSnackbarMessage({
                message: 'Driver saved successfully.',
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
        } catch (error) {
            showSnackbarMessage({
                message: 'Unable to save driver.',
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
            onClose();
        }
    };

    return (
        <ConfirmDialog
            title="Edit Driver"
            isOpen={isOpen}
            acceptButton="Save"
            cancelButton="Cancel"
            disabledSubmit={
                selectedDriverIntegrationId === currentDriverIntegrationId || (!selectedDriverIntegrationId && !currentDriverIntegrationId)
            }
            isNewSubmitButtonStyle
            contentMessage={
                <div style={{ width: isEncompass ? '300px' : '292px' }}>
                    <SingleSelect
                        label="Driver"
                        disabled={false}
                        items={driverOptions}
                        selectionChanged={(option) => setSelectedDriverIntegrationId(option?.value ?? null)}
                        isMobileView={false}
                        selectedValue={selectedDriverIntegrationId}
                        clearable={true}
                    ></SingleSelect>
                </div>
            }
            handleModalClosed={() => onClose()}
            handleSubmit={handleOnSubmit}
        ></ConfirmDialog>
    );
};

export default EditEventDriverDialog;
